<template>
  <li v-if="showBell" class="nav-item dropdown d-flex justify-content-center align-items-center">
    <a class="nav-link cursor-pointer" data-toggle="dropdown" style="display: flex; align-items: center; gap: 0.6rem; top: 2px">
      <img :src="bellIcon" alt="Notifications" style="width: 20px" />
      <transition name="badge-slide">
        <span v-if="unreadCount > 0" class="badge badge-warning navbar-badge" style="right: -5px; top: 0px; background-color: #f84518">
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
      </transition>
    </a>

    <div class="dropdown-menu dropdown-menu-lg dropdown-menu-right" style="width: 300px">
      <span class="dropdown-item dropdown-header">Notifications</span>
      <div class="dropdown-divider"></div>

      <div class="notification-scroll" @scroll.passive="handleScroll">
        <div v-if="notifications.length === 0" class="text-center text-muted py-3">No notifications found.</div>
        <div
          v-for="(note, index) in notifications"
          :key="note.id"
          class="notification-item dropdown-item"
          :class="{ 'notification-unread': note.unread, 'notification-read': !note.unread }"
          @click="markAsRead(index)"
          role="button"
          tabindex="0"
          @keydown.enter="markAsRead(index)"
        >
          <!-- Title -->
          <div class="notification-title">
            <i class="fas fa-exclamation-circle text-warning me-2"></i>
            <strong>{{ note.title }}!</strong>
          </div>

          <!-- Body -->
          <div class="notification-body">
            <div v-if="note.type === 1 || note.type === 2" v-html="note.message" @click="handleHtmlClick" class="notification-html"></div>

            <template v-if="note.type === 3">
              <div v-if="!note.counterSale && note.status === 3" class="notification-line">
                <span class="label">Jobcard ID:</span>
                <span class="value" @click.stop="goToQuotationByJobcard(note.jobCardId, index)" title="" v-b-tooltip.hover>
                  {{ note.jobCardId }}
                </span>
              </div>

              <div class="notification-line">
                <span class="label">Quotation ID:</span>
                <span class="value" @click.stop="goToQuotationByQuotation(note.quotationId, index)" title="" v-b-tooltip.hover>
                  {{ note.quotationId }}
                </span>
              </div>

              <div class="notification-line">
                <span class="label">Date:</span>
                <span>{{ formatTimestamp(note.timestamp) }}</span>
              </div>
            </template>
          </div>
        </div>
        <b-overlay :show="isLoadingMore" rounded="sm" opacity="0" spinner-variant="primary" class="text-center"></b-overlay>
        <div v-if="noMoreNotifications" class="text-center py-2 text-muted">No more notifications</div>
      </div>
    </div>
  </li>
</template>

<script>
  import db from '@/firebase';
  import UserService from '@/services/UserService';
  import moment from 'moment';

  import { collection, doc, getDocs, limit, onSnapshot, orderBy, query, startAfter, updateDoc, where } from 'firebase/firestore';

  export default {
    name: 'NotificationList',
    data() {
      return {
        notifications: [],
        unreadCount: 0,
        showBell: false,
        groups: [],
        currentUserId: null,
        unsubscribe: null,
        now: moment(),
        intervalId: null,
        bellIcon: require('@/assets/icons/bell1.png'),
        lastVisible: null,
        isLoadingMore: false,
        noMoreNotifications: false,
        pageSize: 15,
        unreadInterval: null,
      };
    },
    methods: {
      async listenToNotifications() {
        try {
          const response = await UserService.getCurrentUser();
          this.groups = Array.isArray(response?.roles) ? response.roles : [];
          this.currentUserId = response?.id || null;

          if (!this.currentUserId) {
            console.warn('No valid user ID found. Notification listener aborted.');
            return;
          }

          const q = query(collection(db, 'afs_notifications'), orderBy('timestamp', 'desc'), limit(this.pageSize));

          this.unsubscribe = onSnapshot(q, (snapshot) => {
            const notes = [];
            let unread = 0;
            let newNotificationPlayed = false;
            let hasRelevantNote = false;

            const changeTypeMap = new Map();
            snapshot.docChanges().forEach((change) => {
              changeTypeMap.set(change.doc.id, change.type);
            });

            const userGroups = this.groups || [];

            snapshot.docs.forEach((docSnap) => {
              const note = docSnap.data();
              note.id = docSnap.id;
              const isRelevant = (note.groupNames || []).some((group) => userGroups.includes(group));
              if (!isRelevant) return;
              hasRelevantNote = true;
              note.unread = !(note.readBy || []).includes(this.currentUserId);
              if (note.unread) unread++;
              notes.push(note);
              if (changeTypeMap.get(note.id) === 'added' && note.unread && !newNotificationPlayed) {
                this.playNotificationSound();
                newNotificationPlayed = true;
              }
            });

            this.notifications = notes;
            // this.unreadCount = unread > 9 ? '9+' : unread;
            this.showBell = hasRelevantNote;

            if (snapshot.docs.length > 0) {
              this.lastVisible = snapshot.docs[snapshot.docs.length - 1];
              this.noMoreNotifications = false;
            }

            this.getTotalUnreadCount();
          });
        } catch (error) {}
      },

      async loadMoreNotifications() {
        if (this.isLoadingMore || this.noMoreNotifications || !this.lastVisible) return;
        this.isLoadingMore = true;

        try {
          const nextQuery = query(collection(db, 'afs_notifications'), orderBy('timestamp', 'desc'), startAfter(this.lastVisible), limit(this.pageSize));

          const snapshot = await getDocs(nextQuery);
          if (snapshot.empty) {
            this.noMoreNotifications = true;
            this.isLoadingMore = false;
            return;
          }

          const moreNotes = [];
          const userGroups = this.groups || [];

          snapshot.docs.forEach((docSnap) => {
            const note = docSnap.data();
            note.id = docSnap.id;
            const isRelevant = (note.groupNames || []).some((group) => userGroups.includes(group));
            if (!isRelevant) return;
            note.unread = !(note.readBy || []).includes(this.currentUserId);
            moreNotes.push(note);
          });

          this.notifications = [...this.notifications, ...moreNotes];
          // Update unread count safely
          const unread = this.notifications.filter((n) => n.unread).length;
          // this.unreadCount = unread > 9 ? '9+' : unread;
          this.lastVisible = snapshot.docs[snapshot.docs.length - 1];

          this.getTotalUnreadCount();
        } catch (error) {
          console.error('Error loading more notifications:', error);
        } finally {
          this.isLoadingMore = false;
        }
      },

      handleScroll(event) {
        const el = event.target;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

        if (nearBottom) {
          this.loadMoreNotifications();
        }
      },

      async getTotalUnreadCount() {
        try {
          if (!this.currentUserId || !this.groups?.length) return;

          const unreadQuery = query(collection(db, 'afs_notifications'), where('groupNames', 'array-contains-any', this.groups));

          const snapshot = await getDocs(unreadQuery);

          const totalUnread = snapshot.docs.filter((doc) => {
            const data = doc.data();
            return !(data.readBy || []).includes(this.currentUserId);
          }).length;

          this.unreadCount = totalUnread;
        } catch (error) {
          console.error('Error fetching total unread count:', error);
        }
      },

      async markAsRead(index) {
        const note = this.notifications[index];
        if (!note || !note.unread) return;

        const currentUserId = this.currentUserId;
        if (!currentUserId) return;
        const alreadyRead = (note.readBy || []).includes(currentUserId);
        if (alreadyRead) return;

        this.notifications[index].unread = false;
        this.unreadCount = Math.max(0, this.unreadCount - 1);

        try {
          const noteRef = doc(db, 'afs_notifications', note.id);
          const updatedReadBy = [...(note.readBy || []), currentUserId];

          await updateDoc(noteRef, { readBy: updatedReadBy });
          this.notifications[index].readBy = updatedReadBy;
        } catch (error) {
          this.notifications[index].unread = true;
          this.unreadCount++;
        }
      },

      formatDate(timestamp) {
        if (!timestamp) return '';
        return timestamp.toDate ? timestamp.toDate().toLocaleString() : new Date(timestamp).toLocaleString();
      },
      copyToClipboard(text) {
        navigator.clipboard.writeText(text).then(() => {});
      },

      goToQuotationByQuotation(quotationId, index) {
        this.markAsRead(index);
        localStorage.setItem(
          'quotationNavigation',
          JSON.stringify({
            type: 'quotationId',
            value: quotationId,
          })
        );

        this.$router
          .push({
            path: '/quotations',
            query: { nav: Date.now() },
          })
          .catch(() => {});
      },

      goToQuotationByJobcard(jobCardId, index) {
        this.markAsRead(index);
        localStorage.setItem(
          'quotationNavigation',
          JSON.stringify({
            type: 'jobcardId',
            value: jobCardId,
          })
        );

        this.$router
          .push({
            path: '/quotations',
            query: { nav: Date.now() },
          })
          .catch(() => {});
      },

      linkJobcard(jobCardId) {
        localStorage.setItem(
          'linkJobcard',
          JSON.stringify({
            type: 'jobcardId',
            value: jobCardId,
          })
        );

        this.$router
          .push({
            path: '/job-cards',
            query: { jobCardId: jobCardId },
          })
          .catch(() => {});
      },

      handleHtmlClick(event) {
        const target = event.target;
        if (target.matches('[data-copy]')) {
          const textToCopy = target.getAttribute('data-copy');

          this.copyToClipboard(textToCopy);

          if (typeof textToCopy === 'string' && textToCopy.startsWith('J')) {
            this.linkJobcard(textToCopy);
          }
        }
      },

      playNotificationSound() {
        const audio = new Audio('/sounds/notification5.mp3');
        audio.volume = 0.6;
        audio.play().catch((err) => {});
      },
      formatTimestamp(timestamp) {
        if (!timestamp?.seconds) return '';
        const millis = timestamp.seconds * 1000 + Math.floor((timestamp.nanoseconds || 0) / 1_000_000);
        return moment(millis).format('DD/MM/YY - hh:mm A');
      },
    },
    mounted() {
      this.listenToNotifications();
    },
    beforeDestroy() {
      if (this.unsubscribe) this.unsubscribe();
    },
  };
</script>

<style scoped>
  .badge-warning {
    background-color: #f84518 !important;
  }

  .copyable:hover {
    text-decoration: underline;
  }
  .notification-item {
    transition: background-color 0.2s ease-in-out;
    padding: 10px 15px;
    border-bottom: 1px solid #f1f1f1;
  }
  .notification-item:hover {
    background-color: #f8f9fa;
  }
  .notification-scroll {
    max-height: 350px;
    overflow-y: auto;
    padding: 0rem;
  }

  .notification-item {
    padding: 0.75rem 1rem;
    font-size: 0.8rem;
    line-height: 1.6;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .notification-item:last-child {
    border-bottom: none;
  }

  .notification-item:hover {
    background-color: #f9f9f9;
  }

  .notification-title {
    display: flex;
    align-items: center;
    margin-bottom: 0.2rem;
    font-weight: 600;
    font-size: 0.8rem;
  }

  .notification-body {
    color: rgb(0, 0, 0);
    font-size: 0.8rem;
    padding-left: 1.4rem;
  }

  .notification-html {
    margin-bottom: 0rem;
    white-space: normal;
  }

  .notification-line {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 0.3rem;
  }

  .notification-line .label {
    font-weight: 500;
    color: rgb(0, 0, 0);
    margin-right: 0.3rem;
  }

  .notification-line .value {
    color: #0d6efd;
    font-weight: 500;
    cursor: pointer;
    font-size: 0.8rem;
  }
  .notification-unread {
    background-color: #f0f8ff;
    font-weight: 500;
  }

  .notification-read {
    background-color: #ffffff;
  }
</style>
